import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, Crown } from 'lucide-react';
import { MemberRoleSelect } from './MemberRoleSelect';
import type { ClientMember, ClientMemberCreate, ClientMemberUpdate, MemberRole } from '@/types/client';
import clientMembersService from '@/services/clientMembersService';
import { useAlert } from '@/components/AlertProvider';
import { useAuthStore } from '@/stores/authStore';
import { sanitizePhoneInput } from '@/utils/phoneUtils';

interface ClientMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  member?: ClientMember | null;
  clientId: number;
}

export const ClientMemberModal: React.FC<ClientMemberModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  member,
  clientId,
}) => {
  const [loading, setLoading] = useState(false);
  const { user } = useAuthStore();
  const { showSuccess, showError } = useAlert();

  // Determine which roles to exclude based on current user
  const getExcludedRoles = (): MemberRole[] => {
    const excludedRoles: MemberRole[] = [];

    console.log('[DEBUG] ClientMemberModal - Full user object:', user);
    console.log('[DEBUG] ClientMemberModal - user:', user);
    console.log('[DEBUG] ClientMemberModal - user?.member_role:', user?.member_role);
    console.log('[DEBUG] ClientMemberModal - user?.client_type:', (user as any)?.client_type);

    // Check different conditions for excluding purchaser role
    const isBuyerMemberIndependent = user?.role === 'client_member' && user?.member_role === 'independent';
    const isIndependentRole = user?.role === 'independent';
    const isBuyerWithIndependentType = user?.role === 'client' && (user as any)?.client_type === 'independent';
    
    console.log('[DEBUG] Checking conditions:');
    console.log('[DEBUG] - isBuyerMemberIndependent:', isBuyerMemberIndependent);
    console.log('[DEBUG] - isIndependentRole:', isIndependentRole);
    console.log('[DEBUG] - isBuyerWithIndependentType:', isBuyerWithIndependentType);

    // If current user is a Client member with independent role OR Client with independent type, exclude purchaser
    if (isBuyerMemberIndependent || isIndependentRole || isBuyerWithIndependentType) {
      console.log('[DEBUG] ✅ Condition matched! Excluding purchaser role for independent user/Client');
      excludedRoles.push('purchaser');
    } else {
      console.log('[DEBUG] ❌ Condition NOT matched - no roles excluded');
    }

    console.log('[DEBUG] Final excluded roles:', excludedRoles);
    return excludedRoles;
  };
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    member_type: 'employe' as MemberRole,
    access_all_sales_points: true,
    sales_point_ids: [] as number[],
    can_create_sales_points: false,
    can_create_orders: false,
    can_manage_invoices: false,
    receive_notifications: true,
    is_active: true,
    notes: '',
  });

  useEffect(() => {
    if (member) {
      setFormData({
        first_name: member.first_name,
        last_name: member.last_name,
        email: member.email,
        phone: member.phone || '',
        password: '', // Ne pas pré-remplir le mot de passe
        member_type: member.member_type,
        access_all_sales_points: member.access_all_sales_points,
        sales_point_ids: member.sales_point_ids,
        can_create_sales_points: member.can_create_sales_points || false,
        can_create_orders: member.can_create_orders || false,
        can_manage_invoices: member.can_manage_invoices || false,
        receive_notifications: member.receive_notifications ?? true,
        is_active: member.is_active,
        notes: member.notes || '',
      });
    } else {
      // Reset pour nouveau contact
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        member_type: 'employe',
        access_all_sales_points: true,
        sales_point_ids: [],
        can_create_sales_points: false,
        can_create_orders: false,
        can_manage_invoices: false,
        receive_notifications: true,
        is_active: true,
        notes: '',
      });
    }
  }, [member, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation: clientId doit être valide pour créer un nouveau contact
    if (!member && (!clientId || clientId === 0)) {
      showError('ID client invalide');
      return;
    }

    try {
      setLoading(true);

      if (member) {
        // Mise à jour
        const updateData: ClientMemberUpdate = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone || undefined,
          member_type: formData.member_type,
          access_all_sales_points: formData.access_all_sales_points,
          sales_point_ids: formData.access_all_sales_points ? [] : formData.sales_point_ids,
          can_create_sales_points: formData.can_create_sales_points,
          can_create_orders: formData.can_create_orders,
          can_manage_invoices: formData.can_manage_invoices,
          receive_notifications: formData.receive_notifications,
          is_active: formData.is_active,
          notes: formData.notes || undefined,
        };

        // Ajouter le mot de passe seulement s'il est fourni
        if (formData.password) {
          updateData.password = formData.password;
        }

        await clientMembersService.update(member.id, updateData);
        showSuccess('Membre mis à jour avec succès');
      } else {
        // Création
        const createData: ClientMemberCreate = {
          client_id: clientId,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone || undefined,
          password: formData.password,
          member_type: formData.member_type,
          access_all_sales_points: formData.access_all_sales_points,
          sales_point_ids: formData.access_all_sales_points ? [] : formData.sales_point_ids,
          can_create_sales_points: formData.can_create_sales_points,
          can_create_orders: formData.can_create_orders,
          can_manage_invoices: formData.can_manage_invoices,
          receive_notifications: formData.receive_notifications,
          is_active: formData.is_active,
          notes: formData.notes || undefined,
        };

        await clientMembersService.create(createData);
        showSuccess('Membre créé avec succès');
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      showError(error.response?.data?.detail || (member ? 'Erreur lors de la mise à jour' : 'Erreur lors de la création'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
            {member ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
          </h2>
          <button
            onClick={onClose}
            aria-label="Fermer"
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="px-6 py-4 space-y-4">
            {/* Avertissement si clientId invalide */}
            {!member && (!clientId || clientId === 0) && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-sm text-red-800 dark:text-red-300">
                  ⚠️ Impossible de créer un utilisateur : profil client non chargé. Veuillez rafraîchir la page.
                </p>
              </div>
            )}
            
            {/* Nom et Prénom */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Prénom *
                </label>
                <input
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-slate-800 dark:text-white"
                  placeholder="Jean"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Nom *
                </label>
                <input
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-slate-800 dark:text-white"
                  placeholder="Dupont"
                />
              </div>
            </div>

            {/* Email et Téléphone */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-slate-800 dark:text-white"
                  placeholder="jean.dupont@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Téléphone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: sanitizePhoneInput(e.target.value) })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-slate-800 dark:text-white"
                  placeholder="+41 79 123 45 67"
                />
              </div>
            </div>

            {/* Mot de passe */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Mot de passe {!member && '*'}
              </label>
              <input
                type="password"
                required={!member}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-slate-800 dark:text-white"
                placeholder={member ? 'Laisser vide pour ne pas changer' : 'Mot de passe sécurisé'}
              />
              {member && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Laisser vide pour conserver le mot de passe actuel
                </p>
              )}
            </div>

            {/* Type de contact */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Type d'utilisateur *
              </label>
              <MemberRoleSelect
                value={formData.member_type}
                onChange={(member_type) => setFormData({ ...formData, member_type })}
                excludeRoles={getExcludedRoles()}
                required
              />
            </div>

            {/* Accès aux points de vente - Hidden for gerant members */}
            {formData.member_type !== 'gerant' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Accès aux points de vente
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.access_all_sales_points}
                      onChange={() => setFormData({ ...formData, access_all_sales_points: true })}
                      className="text-orange-600 focus:ring-orange-500"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      Tous les points de vente
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={!formData.access_all_sales_points}
                      onChange={() => setFormData({ ...formData, access_all_sales_points: false })}
                      className="text-orange-600 focus:ring-orange-500"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      Sélection spécifique (à configurer après création)
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Info message for gerant members */}
            {formData.member_type === 'gerant' && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-green-800 dark:text-green-200">
                      Gérant - Tous les droits
                    </h4>
                    <p className="text-sm text-green-800 dark:text-green-200">
                      <strong>Gérant :</strong> Cet utilisateur aura accès à toutes les fonctionnalités
                      et pourra gérer d'autres utilisateurs. Il dispose de tous les droits sur le compte client.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Statut */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="receive_notifications"
                checked={formData.receive_notifications}
                onChange={(e) => setFormData({ ...formData, receive_notifications: e.target.checked })}
                className="rounded text-orange-600 focus:ring-orange-500"
              />
              <label htmlFor="receive_notifications" className="text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
                Notification mail
              </label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded text-orange-600 focus:ring-orange-500"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
                Compte actif
              </label>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-slate-800 dark:text-white resize-none"
                placeholder="Notes internes sur cet utilisateur..."
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || (!member && (!clientId || clientId === 0))}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-lg hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Enregistrement...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>{member ? 'Mettre à jour' : 'Créer'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientMemberModal;
